import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import MensagemAlerta from '../components/MensagemAlerta';

const ToastContext = createContext(null);
const DEFAULT_DURATION = 5000;

const normalizarTipo = (tipo) => {
  if (tipo === 'success' || tipo === 'sucesso') return 'success';
  if (tipo === 'warning' || tipo === 'aviso') return 'warning';
  return 'error';
};

export const notifyToast = ({ mensagem, message, tipo = 'error', type, duration = DEFAULT_DURATION }) => {
  const text = mensagem || message;
  if (!text) return;

  window.dispatchEvent(new CustomEvent('app:toast', {
    detail: {
      mensagem: text,
      tipo: type || tipo,
      duration
    }
  }));
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const nextId = useRef(1);

  const removeToast = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback((mensagem, tipo = 'error', options = {}) => {
    if (!mensagem) return null;

    const id = nextId.current;
    nextId.current += 1;

    setToasts((current) => [
      ...current,
      {
        id,
        mensagem,
        tipo: normalizarTipo(tipo)
      }
    ]);

    const duration = options.duration ?? DEFAULT_DURATION;
    if (duration > 0) {
      window.setTimeout(() => removeToast(id), duration);
    }

    return id;
  }, [removeToast]);

  useEffect(() => {
    const handleToast = (event) => {
      const { mensagem, message, tipo, type, duration } = event.detail || {};
      showToast(mensagem || message, type || tipo, { duration });
    };

    window.addEventListener('app:toast', handleToast);
    return () => window.removeEventListener('app:toast', handleToast);
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, removeToast }}>
      {children}
      <div className="toast-container" role="status" aria-live="polite" aria-atomic="false">
        {toasts.map((toast) => (
          <MensagemAlerta
            key={toast.id}
            tipo={toast.tipo}
            mensagem={toast.mensagem}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast deve ser usado dentro de ToastProvider.');
  }
  return context;
};
